import { useEffect, useState } from "react";
import { Target, Play, Pause, Zap, Copy, Send, CheckCircle2, XCircle } from "lucide-react";
import { api, WHATSAPP_NUMBER } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AutopilotPage() {
  const { user, refresh } = useAuth();
  const [goals, setGoals] = useState([]);
  const [opps, setOpps] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(null);
  const [planBusy, setPlanBusy] = useState(null);
  const [assist, setAssist] = useState(null);
  const [assistBusy, setAssistBusy] = useState(null);

  const ladder = async (g, day, mode) => {
    setAssistBusy(`${g.goal_id}-${day}-${mode}`);
    try {
      const { data } = await api.post(`/autopilot/goals/${g.goal_id}/plan/${day}/assist`, { mode });
      setAssist({ goalId: g.goal_id, day, text: data.help });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setAssistBusy(null); }
  };

  const getPlan = async (g) => {
    setPlanBusy(g.goal_id);
    try {
      await api.post(`/autopilot/goals/${g.goal_id}/plan`);
      toast.success("Your 7-day execution plan is ready 📋");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setPlanBusy(null); }
  };

  const toggleTask = async (g, day) => {
    try {
      const { data } = await api.post(`/autopilot/goals/${g.goal_id}/plan/${day}/toggle`);
      if (data.done === data.total) toast.success("🏆 WIN! Plan complete — you executed all 7 days!");
      else if (data.next_action) toast.success(`Done ✓ Next: ${data.next_action}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const load = async () => {
    try {
      const [{ data: g }, { data: o }] = await Promise.all([
        api.get("/autopilot/goals"), api.get("/autopilot/opportunities"),
      ]);
      setGoals(g); setOpps(o);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (text.trim().length < 5) return toast.error("Describe your goal first");
    setBusy(true);
    try {
      await api.post("/autopilot/goals", { text: text.trim() });
      toast.success("Goal set — Zuntria will hunt daily 🎯");
      setText(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggle = async (g) => {
    await api.patch(`/autopilot/goals/${g.goal_id}`, { active: !g.active });
    load();
  };

  const runNow = async (g) => {
    setRunning(g.goal_id);
    try {
      await api.post(`/autopilot/goals/${g.goal_id}/run-now`);
      toast.success("Hunt complete — fresh opportunities below ⚡");
      load(); refresh();
    } catch (err) { toast.error(err?.response?.data?.detail || "Run failed"); }
    finally { setRunning(null); }
  };

  const mark = async (o, status) => {
    await api.patch(`/autopilot/opportunities/${o.opp_id}`, { status });
    load();
  };

  const copyPitch = (o) => {
    navigator.clipboard.writeText(o.pitch);
    toast.success("Pitch copied — paste it anywhere");
  };

  const sendWhatsApp = (o) => {
    window.open(`https://wa.me/?text=${encodeURIComponent(o.pitch)}`, "_blank");
    mark(o, "sent");
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Goal Autopilot</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Set it once. Zuntria hunts daily.</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Describe a goal in plain words — Zuntria turns it into a daily Client Hunter that finds opportunities and drafts your pitches.
        Each run costs <strong className="text-amber-400">25 ⚡ credits</strong> (you have {Number(user?.zuntria_credits || 0)} — <a href="/referrals" className="text-primary hover:underline">earn more</a>). Max 2 runs/day per goal.
      </p>

      <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="autopilot-create">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
          placeholder='e.g. "Find me three painting jobs in Manchester this week, minimum £150"'
          className="text-sm"
          data-testid="autopilot-goal-input" />
        <Button onClick={create} disabled={busy} className="mt-3" data-testid="autopilot-goal-submit">
          <Target className="mr-2 h-4 w-4" /> {busy ? "Setting up…" : "Start Autopilot Goal"}
        </Button>
      </div>

      {goals.length > 0 && (
        <div className="mt-8 space-y-3" data-testid="autopilot-goals">
          <h2 className="font-display text-xl">Your goals</h2>
          {goals.map((g) => (
            <div key={g.goal_id} className="rounded-2xl border border-border bg-card p-4" data-testid={`goal-${g.goal_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium">{g.objective}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {g.location} · min £{g.min_value_gbp} · {g.active ? "🟢 hunting daily" : "⏸ paused"}
                    {g.last_run ? ` · last run ${g.last_run.slice(0, 10)}` : ""}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => runNow(g)} disabled={running === g.goal_id} data-testid={`run-${g.goal_id}`}>
                    <Zap className="mr-1 h-3.5 w-3.5 text-amber-400" /> {running === g.goal_id ? "Hunting…" : "Run now (25⚡)"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => toggle(g)} data-testid={`toggle-${g.goal_id}`}>
                    {g.active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              {!g.plan ? (
                <Button size="sm" variant="outline" className="mt-3" onClick={() => getPlan(g)} disabled={planBusy === g.goal_id} data-testid={`plan-${g.goal_id}`}>
                  📋 {planBusy === g.goal_id ? "Building your plan…" : "Get my 7-day plan"}
                </Button>
              ) : (
                <div className="mt-3 rounded-xl border border-border bg-background/60 p-3" data-testid={`plan-list-${g.goal_id}`}>
                  {(() => { const done = g.plan.filter((t) => t.done).length; return (
                    <>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium">{done === g.plan.length ? "🏆 Plan complete — WIN!" : `7-day plan · ${done}/${g.plan.length} done`}</span>
                        <span className="text-muted-foreground">{Math.round((done / g.plan.length) * 100)}%</span>
                      </div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-secondary">
                        <div className="h-full rounded-full bg-emerald-400 transition-all" style={{ width: `${(done / g.plan.length) * 100}%` }} />
                      </div>
                    </>
                  ); })()}
                  <ul className="mt-2 space-y-1.5">
                    {g.plan.map((t) => (
                      <li key={t.day} className="text-sm">
                        <div className="flex items-start gap-2">
                          <button onClick={() => toggleTask(g, t.day)} data-testid={`task-${g.goal_id}-${t.day}`}
                            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[10px] ${t.done ? "border-emerald-400 bg-emerald-400/20 text-emerald-400" : "border-border text-transparent hover:border-primary"}`}>
                            ✓
                          </button>
                          <span className={`min-w-0 ${t.done ? "text-muted-foreground line-through" : ""}`}>
                            <strong className="text-xs text-muted-foreground">Day {t.day}:</strong> {t.task}
                          </span>
                        </div>
                        {!t.done && (
                          <div className="ml-7 mt-1 flex flex-wrap gap-1.5">
                            {[["breakdown", "Break it down"], ["start", "Help me start"], ["explain", "Explain it"]].map(([mode, label]) => (
                              <button key={mode} onClick={() => ladder(g, t.day, mode)} disabled={!!assistBusy}
                                className="rounded-full bg-secondary px-2.5 py-0.5 text-[10px] text-muted-foreground transition-colors hover:bg-primary/15 hover:text-primary"
                                data-testid={`ladder-${mode}-${g.goal_id}-${t.day}`}>
                                {assistBusy === `${g.goal_id}-${t.day}-${mode}` ? "Thinking…" : label}
                              </button>
                            ))}
                          </div>
                        )}
                        {assist && assist.goalId === g.goal_id && assist.day === t.day && (
                          <div className="ml-7 mt-2 whitespace-pre-wrap rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs leading-relaxed" data-testid={`ladder-help-${g.goal_id}-${t.day}`}>
                            {assist.text}
                            <button onClick={() => setAssist(null)} className="mt-2 block text-[10px] text-muted-foreground hover:text-foreground">Close ✕</button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-8" data-testid="autopilot-opps">
        <h2 className="font-display text-xl">Opportunities found</h2>
        {opps.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Nothing yet — set a goal above and hit "Run now".</p>
        ) : (
          <div className="mt-3 space-y-3">
            {opps.map((o) => (
              <div key={o.opp_id} className={`rounded-2xl border p-4 ${o.status === "won" ? "border-emerald-500/50 bg-emerald-500/5" : "border-border bg-card"}`} data-testid={`opp-${o.opp_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium">{o.title}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">📍 {o.where_to_find}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 text-xs">
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">fit {o.score}%</span>
                    <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-emerald-400">~£{Math.round(o.est_value_gbp)}</span>
                  </div>
                </div>
                <p className="mt-2 rounded-lg bg-secondary/60 p-3 text-sm">{o.pitch}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => copyPitch(o)} data-testid={`copy-${o.opp_id}`}><Copy className="mr-1 h-3.5 w-3.5" /> Copy pitch</Button>
                  <Button size="sm" className="bg-[#25D366] hover:bg-[#1ebd5b]" onClick={() => sendWhatsApp(o)} data-testid={`wa-${o.opp_id}`}><Send className="mr-1 h-3.5 w-3.5" /> Send via WhatsApp</Button>
                  {o.status !== "won" && <Button size="sm" variant="ghost" onClick={() => mark(o, "won")} data-testid={`won-${o.opp_id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5 text-emerald-500" /> Won it</Button>}
                  {o.status !== "rejected" && <Button size="sm" variant="ghost" onClick={() => mark(o, "rejected")} data-testid={`skip-${o.opp_id}`}><XCircle className="mr-1 h-3.5 w-3.5" /> Skip</Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
